<template>
  <div>
    <div class="text-subtitle-2 mb-2">Outputs</div>
    <p class="caption text--secondary mb-2">
      Select which outputs this event will turn on at the start time and off at the end time.
    </p>

    <v-alert v-if="!deviceGuid" dense outlined type="info" class="mb-2">
      Select a device to load outputs.
    </v-alert>

    <v-progress-linear v-else-if="loading" indeterminate color="primary" class="mb-2" />

    <v-alert v-else-if="!isEncoder" dense outlined type="warning" class="mb-2">
      Scheduled outputs are only available on encoder devices.
    </v-alert>

    <v-alert
      v-else-if="!choices.length"
      dense
      outlined
      type="info"
      class="mb-2"
    >
      No configured outputs found on this device. Configure outputs on the device first.
    </v-alert>

    <v-list v-else dense class="scheduler-output-list">
      <v-list-item v-for="out in choices" :key="out.id">
        <v-list-item-action class="my-0">
          <v-checkbox
            v-model="selectedIds"
            :value="out.id"
            :disabled="disabled"
            hide-details
            class="mt-0"
          />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>{{ out.name }} <span class="text--secondary">(ID {{ out.id }})</span></v-list-item-title>
          <v-list-item-subtitle>{{ out.type }} · {{ out.resolution }}</v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
export default {
  name: 'SchedulerOutputPicker',

  props: {
    deviceGuid: { type: String, default: null },
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      choices: [],
      device: null,
      shadowUnwatch: null,
      outputsFetchStarted: false,
    }
  },

  computed: {
    isEncoder() {
      return this.device && this.device.device_type === 'encoder'
    },

    selectedIds: {
      get() {
        return this.value || []
      },
      set(ids) {
        const normalized = [...ids]
          .map((id) => parseInt(id, 10))
          .filter((n) => !isNaN(n))
          .sort((a, b) => a - b)
        this.$emit('input', normalized)
      },
    },
  },

  watch: {
    deviceGuid: {
      immediate: true,
      handler() {
        this.bindDevice()
      },
    },
  },

  beforeDestroy() {
    this.teardownShadowWatch()
  },

  methods: {
    teardownShadowWatch() {
      if (this.shadowUnwatch) {
        this.shadowUnwatch()
        this.shadowUnwatch = null
      }
      this.outputsFetchStarted = false
    },

    bindDevice() {
      this.teardownShadowWatch()
      this.choices = []
      this.device = null

      if (!this.deviceGuid) {
        this.loading = false
        return
      }

      this.loading = true
      const guid = this.deviceGuid
      this.$devices.getDevice(guid)

      this.shadowUnwatch = this.$watch(
        () => this.outputsWatchSource(guid),
        (source) => this.onOutputsWatchSource(source, guid),
        { immediate: true, deep: true }
      )
    },

    outputsWatchSource(guid) {
      const dev = this.$devices.getDevice(guid)
      const type = dev ? dev.device_type : null
      const shadow = this.$deviceShadows.getDeviceShadow(guid, 'Outputs')
      const state = shadow.reported && shadow.reported.state
      return {
        type,
        state,
        fetching: shadow.fetching,
      }
    },

    onOutputsWatchSource(source, guid) {
      if (guid !== this.deviceGuid) {
        return
      }

      this.device = this.$devices.getDevice(guid)

      if (!source.type || source.type === false) {
        this.loading = true
        this.choices = []
        return
      }

      if (source.type !== 'encoder') {
        this.loading = false
        this.choices = []
        return
      }

      this.ensureEncoderOutputsFetched(guid)
      this.refreshChoices()
    },

    ensureEncoderOutputsFetched(guid) {
      const device = this.$devices.getDevice(guid)
      if (device && typeof device.syncDocuments === 'function') {
        device.syncDocuments(true)
      }

      const shadow = this.$deviceShadows.getDeviceShadow(guid, 'Outputs')
      const hasReported =
        shadow.reported &&
        Array.isArray(shadow.reported.state) &&
        shadow.reported.state.length > 0

      if (hasReported || shadow.fetching || this.outputsFetchStarted) {
        return
      }

      this.outputsFetchStarted = true
      shadow.fetchShadow(true)
    },

    refreshChoices() {
      if (!this.device || !this.isEncoder) {
        return
      }

      const shadow = this.$deviceShadows.getDeviceShadow(this.deviceGuid, 'Outputs')
      if (shadow.fetching && !shadow.reported) {
        this.loading = true
        return
      }

      this.choices = this.$helpers.schedulerOutputChoices(this.device)
      this.loading = false
    },
  },
}
</script>
